/** The machine trailer every rendered vault file ends with — `FileVaultSerializer.TRAILER_MARKER`
 *  in the app. */
const TRAILER_MARKER = '<!--pondering:unit ';

/**
 * [text] with its trailer stating that an edit was made at [atMs] (`e`), the rest of the trailer
 * as the render wrote it — so the file still ranks as that render, and a device that takes the
 * edit dates it by [atMs]. The time has to be the backend's, never this browser's clock.
 *
 * A text with no trailer is returned as it is: a new file has to reach a device without one to be
 * minted. The LAST marker is the trailer, since prose may quote one.
 */
export function withEditedAt(text: string, atMs: number): string {
  const at = text.lastIndexOf(TRAILER_MARKER);
  if (at < 0) return text;
  const jsonStart = at + TRAILER_MARKER.length;
  const jsonEnd = text.indexOf('-->', jsonStart);
  if (jsonEnd < 0) return text;
  let trailer: unknown;
  try {
    trailer = JSON.parse(text.slice(jsonStart, jsonEnd).trim());
  } catch {
    return text;
  }
  if (trailer === null || typeof trailer !== 'object' || Array.isArray(trailer)) return text;
  const stamped = { ...(trailer as Record<string, unknown>), e: atMs };
  return text.slice(0, jsonStart) + JSON.stringify(stamped) + text.slice(jsonEnd);
}

/**
 * [text], a file being made, stating in its front matter when it was made at [atMs] (`created:`).
 * Every device that mints it dates the unit by this instead of by its own clock, so the births
 * agree; the time has to be the backend's. A text with a trailer is a render, and one without
 * front matter, or already saying when it was made, is returned as it is.
 * `FileVaultSerializer.withCreatedAt` in the app.
 */
export function withCreatedAt(text: string, atMs: number): string {
  if (!text.startsWith('---\n') || text.includes(TRAILER_MARKER)) return text;
  const close = text.indexOf('\n---', 3);
  if (close < 3 || /^created:/m.test(text.slice(0, close))) return text;
  return `${text.slice(0, close)}\ncreated: ${new Date(atMs).toISOString()}${text.slice(close)}`;
}

/** When a file made without a render says it was made (`created:`), or null. */
export function createdAtMs(text: string): number | null {
  if (!text.startsWith('---\n')) return null;
  const close = text.indexOf('\n---', 3);
  if (close < 4) return null;
  const value = /^created:[ \t]*(\S+)[ \t]*$/m.exec(text.slice(4, close))?.[1];
  const ms = value ? Date.parse(value) : NaN;
  return Number.isNaN(ms) ? null : ms;
}

/**
 * When the file last changed, by what it carries itself: the later of its
 * render's content and state stamps (`v`) and an edit made since without a
 * render (`e`) — or, for a file no device has rendered yet, when it was made.
 * A third `v` stamp is left out: it moves for rows a device changed without
 * anyone changing the unit. Null when the file says none of these.
 */
export function lastChangedMs(text: string): number | null {
  const at = text.lastIndexOf(TRAILER_MARKER);
  if (at < 0) return createdAtMs(text);
  const jsonStart = at + TRAILER_MARKER.length;
  const jsonEnd = text.indexOf('-->', jsonStart);
  if (jsonEnd < 0) return null;
  let trailer: unknown;
  try {
    trailer = JSON.parse(text.slice(jsonStart, jsonEnd).trim());
  } catch {
    return null;
  }
  if (trailer === null || typeof trailer !== 'object' || Array.isArray(trailer)) return null;
  const { v, e } = trailer as { v?: unknown; e?: unknown };
  const stamps = [...(Array.isArray(v) ? v.slice(0, 2) : []), e].filter((n): n is number => typeof n === 'number');
  return stamps.length > 0 ? Math.max(...stamps) : null;
}

/**
 * What this head can still read — `FileVaultSerializer.COMPAT_LEVEL` in the app, raised with it.
 * A file stating a level above it (`r`) was written by an app that carries what this head would
 * drop, so the head never changes it.
 */
export const COMPAT_LEVEL = 0;

/** The largest level the app can hold; a stated level it cannot read as a number is read as this. */
const MAX_LEVEL = 2 ** 31 - 1;
const STATED_LEVEL = /"r"\s*:/;
const PLAIN_LEVEL = /^\d+$/;

/**
 * The level the app that wrote [text] states in its trailer; 0 for a file stating none —
 * `FileVaultSerializer.compatLevelOf` in the app, which this reads the same way. A level written
 * as a plain whole number reads as itself; any other stated level (a trailer that cannot be read
 * or is not an object, a level that is not a whole number or is too big for one) reads as newer
 * than every build, so the head never writes over a file the app might read as newer. A trailer
 * missing its end reads to the end of the file, as the app reads it.
 */
export function compatLevelOf(text: string): number {
  const at = text.lastIndexOf(TRAILER_MARKER);
  if (at < 0) return 0;
  const jsonStart = at + TRAILER_MARKER.length;
  const jsonEnd = text.indexOf('-->', jsonStart);
  const json = text.slice(jsonStart, jsonEnd < 0 ? undefined : jsonEnd).trim();
  if (!STATED_LEVEL.test(json)) return 0;
  let trailer: unknown;
  try {
    trailer = JSON.parse(json);
  } catch {
    return Number.POSITIVE_INFINITY;
  }
  if (trailer === null || typeof trailer !== 'object' || Array.isArray(trailer)) return Number.POSITIVE_INFINITY;
  if (!('r' in trailer)) return 0;
  const stated = topLevelValueText(json, 'r');
  const level = stated !== null && PLAIN_LEVEL.test(stated) ? Number(stated) : Number.POSITIVE_INFINITY;
  return level <= MAX_LEVEL ? level : Number.POSITIVE_INFINITY;
}

/**
 * The text of the value [key] has at the top of [json], an object JSON.parse has read — the last
 * one, as JSON.parse keeps the last of a repeated key — or null where it has none. The text, not
 * the value: `2.0` is no plain level, and JSON.parse would read it as 2.
 */
function topLevelValueText(json: string, key: string): string | null {
  let found: string | null = null;
  let at = json.indexOf('{') + 1;
  while (at > 0 && at < json.length) {
    const keyStart = json.indexOf('"', at);
    if (keyStart < 0) break;
    const keyEnd = stringEnd(json, keyStart);
    const name: unknown = JSON.parse(json.slice(keyStart, keyEnd + 1));
    const valueStart = json.indexOf(':', keyEnd + 1) + 1;
    let depth = 0;
    let end = valueStart;
    for (; end < json.length; end++) {
      const ch = json[end];
      if (ch === '"') end = stringEnd(json, end);
      else if (ch === '{' || ch === '[') depth++;
      else if (ch === '}' || ch === ']') {
        if (depth === 0) break;
        depth--;
      } else if (ch === ',' && depth === 0) break;
    }
    if (name === key) found = json.slice(valueStart, end).trim();
    if (json[end] !== ',') break;
    at = end + 1;
  }
  return found;
}

/** Where the string opening at [start] in [json] closes. */
function stringEnd(json: string, start: number): number {
  let at = start + 1;
  while (at < json.length && json[at] !== '"') at += json[at] === '\\' ? 2 : 1;
  return at;
}

export function isFromANewerApp(text: string): boolean {
  return compatLevelOf(text) > COMPAT_LEVEL;
}
